import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import {
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@roger/ui-components';
import type { SchedulingEmrFlags } from '../../utils/intakeSchedulingEmr';
import {
  getKinnserCheckClearsRecPatch,
  getRogerNewPatientSchedulePatch,
} from './schedulingVisitType';

function ScheduleCheckbox({
  id,
  label,
  checked,
  disabled,
  disabledReason,
  locked,
  onToggle,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  disabledReason?: string;
  locked: boolean;
  onToggle: (checked: boolean) => void | Promise<void>;
}) {
  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='inline-flex items-center gap-2 cursor-not-allowed'>
            <Checkbox
              id={id}
              checked={checked}
              disabled
              onCheckedChange={() => {}}
            />
            <span className='text-sm font-medium text-gray-700'>{label}</span>
          </span>
        </TooltipTrigger>
        {disabledReason && (
          <TooltipContent side='bottom' className='max-w-xs text-xs'>
            {disabledReason}
          </TooltipContent>
        )}
      </Tooltip>
    );
  }

  return (
    <div className='flex items-center gap-2'>
      <Checkbox
        id={id}
        checked={checked}
        disabled={locked && checked}
        onCheckedChange={(val: boolean | 'indeterminate') => {
          if (locked && val !== true) return;
          void onToggle(val === true);
        }}
      />
      <label
        htmlFor={id}
        className={`text-sm font-medium ${
          locked && checked ? 'cursor-default' : 'cursor-pointer'
        }`}
      >
        {label}
      </label>
    </div>
  );
}

type SchedulingEmrFormFields = {
  roger_create_episode?: boolean | null;
  push_to_kinnser?: boolean | null;
  kinnser_create_episode?: boolean | null;
  push_to_roger?: boolean | null;
  kinnser_visit_type?: 'soc' | 'roc' | 'rec' | null;
};

export type SchedulingEmrSelectionHeaderProps<
  TFieldValues extends FieldValues & SchedulingEmrFormFields,
> = {
  control: Control<TFieldValues>;
  setValue: (name: string, value: boolean | string | null) => void;
  saveToServer: (data: Partial<SchedulingEmrFlags>) => void | Promise<void>;
  showRogerSchedulingOption: boolean;
  showKinnserSchedulingOption: boolean;
  schedulingSelectionLocked: boolean;
  /**
   * The intake has already been pushed/scheduled: every checkbox is disabled.
   * schedulingSelectionLocked only enforces pre-push mutual exclusion (the
   * checked box can't be unchecked) — without this flag the unchecked sibling
   * stays clickable and could rewrite a pushed intake's scheduling selection.
   */
  pushCompleted: boolean;
  scheduleRogerBlockedByMatch: boolean;
  scheduleKinnserBlockedByMatch: boolean;
  isWrmClinic: boolean;
  isNewPatient: boolean | null | undefined;
  /** Shared visit-type key (kinnser_visit_type); 'rec' marks the Roger REC option. */
  visitType?: string | null;
  /** Persisted recert date; checking REC with none stamps the default date chain. */
  recertDate?: string | null;
};

const PUSH_LOCKED_REASON =
  'This intake has already been pushed. The scheduling selection can no longer be changed.';

export function SchedulingEmrSelectionHeader<
  TFieldValues extends FieldValues & SchedulingEmrFormFields,
>({
  control,
  setValue,
  saveToServer,
  showRogerSchedulingOption,
  showKinnserSchedulingOption,
  schedulingSelectionLocked,
  pushCompleted,
  scheduleRogerBlockedByMatch,
  scheduleKinnserBlockedByMatch,
  isWrmClinic,
  isNewPatient,
  visitType,
  recertDate,
}: SchedulingEmrSelectionHeaderProps<TFieldValues>) {
  if (!showRogerSchedulingOption && !showKinnserSchedulingOption) {
    return null;
  }

  // Apply a header patch to both the form (setValue per key) and the server.
  const applyPatch = async (patch: Partial<SchedulingEmrFlags>) => {
    for (const [key, value] of Object.entries(patch)) {
      setValue(key, value as boolean | string | null);
    }
    await saveToServer(patch);
  };

  // New patients (or undecided) choose between SOC onboarding and REC (recert)
  // onboarding — two mutually exclusive checkboxes. Existing patients keep the
  // single "Schedule in Roger" option (REC is a new-patient flow only).
  const showRogerNewPatientOptions =
    showRogerSchedulingOption && isNewPatient !== false;
  const rogerBlockedReason =
    'This patient was matched in Kinnser. An existing Kinnser patient cannot be scheduled in Roger.';

  return (
    <div className='flex items-center gap-6'>
      {showRogerNewPatientOptions && (
        <Controller
          name={'roger_create_episode' as Path<TFieldValues>}
          control={control}
          render={({ field: { value, onChange } }) => (
            <>
              <ScheduleCheckbox
                id='createRogerEpisodeEnabled'
                label='Schedule SOC in Roger'
                checked={!!value && visitType !== 'rec'}
                locked={schedulingSelectionLocked}
                disabled={scheduleRogerBlockedByMatch || pushCompleted}
                disabledReason={
                  pushCompleted ? PUSH_LOCKED_REASON : rogerBlockedReason
                }
                onToggle={async isChecked => {
                  if (schedulingSelectionLocked && !isChecked) return;
                  onChange(isChecked);
                  await applyPatch(
                    getRogerNewPatientSchedulePatch('soc', isChecked, visitType)
                  );
                }}
              />
              <ScheduleCheckbox
                id='createRogerRecEpisodeEnabled'
                label='Schedule REC in Roger'
                checked={!!value && visitType === 'rec'}
                locked={schedulingSelectionLocked}
                disabled={scheduleRogerBlockedByMatch || pushCompleted}
                disabledReason={
                  pushCompleted ? PUSH_LOCKED_REASON : rogerBlockedReason
                }
                onToggle={async isChecked => {
                  if (schedulingSelectionLocked && !isChecked) return;
                  onChange(isChecked);
                  await applyPatch(
                    getRogerNewPatientSchedulePatch(
                      'rec',
                      isChecked,
                      visitType,
                      recertDate
                    )
                  );
                }}
              />
            </>
          )}
        />
      )}
      {showRogerSchedulingOption && !showRogerNewPatientOptions && (
        <Controller
          name={'roger_create_episode' as Path<TFieldValues>}
          control={control}
          render={({ field: { value, onChange } }) => (
            <ScheduleCheckbox
              id='createRogerEpisodeEnabled'
              label='Schedule in Roger'
              checked={!!value}
              locked={schedulingSelectionLocked}
              disabled={scheduleRogerBlockedByMatch || pushCompleted}
              disabledReason={
                pushCompleted ? PUSH_LOCKED_REASON : rogerBlockedReason
              }
              onToggle={async isChecked => {
                if (schedulingSelectionLocked && !isChecked) return;
                onChange(isChecked);
                if (isChecked) {
                  setValue('kinnser_create_episode', false);
                  setValue('roger_create_episode', true);
                  setValue('push_to_roger', true);
                  setValue('push_to_kinnser', false);
                  await saveToServer({
                    roger_create_episode: true,
                    kinnser_create_episode: false,
                    push_to_roger: true,
                    push_to_kinnser: false,
                  });
                } else {
                  setValue('push_to_roger', false);
                  await saveToServer({
                    roger_create_episode: false,
                    push_to_roger: false,
                  });
                }
              }}
            />
          )}
        />
      )}
      {showKinnserSchedulingOption && (
        <Controller
          name={'push_to_kinnser' as Path<TFieldValues>}
          control={control}
          render={({ field: { value, onChange } }) => (
            <ScheduleCheckbox
              id='scheduleInKinnser'
              label={isWrmClinic ? 'Push to Kinnser' : 'Schedule in Kinnser'}
              checked={!!value}
              locked={schedulingSelectionLocked}
              disabled={scheduleKinnserBlockedByMatch || pushCompleted}
              disabledReason={
                pushCompleted
                  ? PUSH_LOCKED_REASON
                  : 'This patient was matched in Roger. An existing Roger patient cannot be scheduled in Kinnser.'
              }
              onToggle={async isChecked => {
                if (schedulingSelectionLocked && !isChecked) return;
                onChange(isChecked);
                if (isChecked) {
                  // REC is Roger-only: getKinnserCheckClearsRecPatch clears a
                  // stale 'rec' visit type when switching to Kinnser scheduling.
                  await applyPatch({
                    push_to_kinnser: true,
                    roger_create_episode: false,
                    push_to_roger: false,
                    ...(isNewPatient ? { kinnser_create_episode: true } : {}),
                    ...getKinnserCheckClearsRecPatch(visitType),
                  });
                } else {
                  await applyPatch({
                    push_to_kinnser: false,
                    kinnser_create_episode: false,
                  });
                }
              }}
            />
          )}
        />
      )}
    </div>
  );
}
